import {
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
} from "@assetwatch/ui";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import type { MotionAmplificationJob } from "../../clients/motion-amplification";

function valueOrDash(value: unknown, fallback = "—") {
  return value === undefined || value === null ? fallback : String(value);
}

function parameterRows(job: MotionAmplificationJob, t: TFunction) {
  const p = job.parameters ?? {};
  const rows = [
    [
      t("motionAmplification.name"),
      job.name ||
        job.fileName ||
        t("motionAmplification.jobFallback", { id: job.jobId }),
      t("motionAmplification.displayNameDescription"),
    ],
    ...(job.description
      ? [
          [
            t("motionAmplification.description"),
            job.description,
            t("motionAmplification.optionalContextDescription"),
          ],
        ]
      : []),
    [
      t("motionAmplification.status"),
      job.status,
      t("motionAmplification.currentStatusDescription"),
    ],
    ...(job.status === "Failed"
      ? [
          [
            t("motionAmplification.failurePhase"),
            valueOrDash(job.phase),
            t("motionAmplification.failurePhaseDescription"),
          ],
          [
            t("motionAmplification.failureReason"),
            valueOrDash(job.errorMessage),
            t("motionAmplification.failureReasonDescription"),
          ],
        ]
      : []),
    [
      t("motionAmplification.preset"),
      valueOrDash(job.preset),
      t("motionAmplification.descriptions.preset"),
    ],
    [
      t("motionAmplification.frameRate"),
      valueOrDash(p.fps),
      t("motionAmplification.frameRateDescription"),
    ],
    [
      t("motionAmplification.frequencyStart"),
      valueOrDash(p.frequencyLowHz),
      t("motionAmplification.descriptions.frequencyStart"),
    ],
    [
      t("motionAmplification.frequencyEnd"),
      valueOrDash(p.frequencyHighHz),
      t("motionAmplification.descriptions.frequencyEnd"),
    ],
    [
      t("motionAmplification.alpha"),
      valueOrDash(p.alpha),
      t("motionAmplification.descriptions.alpha"),
    ],
    [
      t("motionAmplification.lambdaC"),
      valueOrDash(p.lambdaC),
      t("motionAmplification.descriptions.lambdaC"),
    ],
    [
      t("motionAmplification.stabilization"),
      p.stabilize
        ? t("motionAmplification.enabled")
        : t("motionAmplification.disabled"),
      t("motionAmplification.descriptions.stabilization"),
    ],
    [
      t("motionAmplification.stabilizationSmoothing"),
      valueOrDash(p.stabilizeSmooth),
      t("motionAmplification.descriptions.stabilizationSmoothing"),
    ],
    [
      t("motionAmplification.maximumWidth"),
      valueOrDash(p.maxWidth, t("motionAmplification.originalWidth")),
      t("motionAmplification.descriptions.maximumWidth"),
    ],
    [
      t("motionAmplification.pyramidLevels"),
      valueOrDash(p.pyramidLevels),
      t("motionAmplification.descriptions.pyramidLevels"),
    ],
    [
      t("motionAmplification.chromaAttenuation"),
      valueOrDash(p.chromAttenuation),
      t("motionAmplification.descriptions.chromaAttenuation"),
    ],
    [
      t("motionAmplification.chunkSize"),
      valueOrDash(p.chunkSize),
      t("motionAmplification.descriptions.chunkSize"),
    ],
    [
      t("motionAmplification.chunkOverlap"),
      valueOrDash(p.overlap, t("motionAmplification.automatic")),
      t("motionAmplification.descriptions.chunkOverlap"),
    ],
    [
      t("motionAmplification.stabilizationPasses"),
      valueOrDash(p.stabilizeSmoothPasses),
      t("motionAmplification.descriptions.stabilizationPasses"),
    ],
    [
      t("motionAmplification.maximumFeatures"),
      valueOrDash(p.stabilizeMaxFeatures),
      t("motionAmplification.descriptions.maximumFeatures"),
    ],
    [
      t("motionAmplification.ransacThreshold"),
      valueOrDash(p.stabilizeRansacPx),
      t("motionAmplification.descriptions.ransacThreshold"),
    ],
    [
      t("motionAmplification.fastThreshold"),
      valueOrDash(p.stabilizeFastThreshold),
      t("motionAmplification.descriptions.fastThreshold"),
    ],
    [
      t("motionAmplification.cropRegion"),
      p.crop ? JSON.stringify(p.crop) : t("motionAmplification.none"),
      t("motionAmplification.descriptions.cropRegion"),
    ],
    [
      t("motionAmplification.heatmapOutput"),
      p.heatmap
        ? t("motionAmplification.enabled")
        : t("motionAmplification.disabled"),
      t("motionAmplification.descriptions.heatmapOutput"),
    ],
    [
      t("motionAmplification.sideBySideOutput"),
      p.sideBySide
        ? t("motionAmplification.enabled")
        : t("motionAmplification.disabled"),
      t("motionAmplification.descriptions.sideBySideOutput"),
    ],
    [
      t("motionAmplification.heatmapAlpha"),
      valueOrDash(p.heatmapAlpha),
      t("motionAmplification.descriptions.heatmapOpacity"),
    ],
  ];
  return rows;
}

export function MotionAmplificationDetailsModal({
  job,
  open,
  onOpenChange,
}: {
  job: MotionAmplificationJob | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={`${t("motionAmplification.videoDetails")} • ${job?.name || job?.fileName || t("motionAmplification.selectVideo")}`}
      size="lg"
    >
      {job && (
        <Tabs defaultValue="parameters">
          <div className="min-w-0 overflow-x-auto">
            <TabsList>
              <TabsTrigger value="parameters">
                {t("motionAmplification.parameters")}
              </TabsTrigger>
              <TabsTrigger value="camera">
                {t("motionAmplification.cameraSettings")}
              </TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="parameters">
            <div className="mt-(--spacing-16)">
              <div className="[&_thead_th]:bg-surface-container [&_tbody_tr_td]:py-(--spacing-4)">
                <Table size="sm">
                  <TableHeader>
                    <TableRow section="header">
                      <TableHead scope="col">
                        {t("motionAmplification.parameter")}
                      </TableHead>
                      <TableHead scope="col">
                        {t("motionAmplification.value")}
                      </TableHead>
                      <TableHead scope="col">
                        {t("motionAmplification.parameterDescription")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parameterRows(job, t).map(([name, value, limits]) => (
                      <TableRow key={name}>
                        <TableCell>{name}</TableCell>
                        <TableCell>{value}</TableCell>
                        <TableCell>{limits}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="camera">
            <div className="mt-(--spacing-16)">
              <Text tone="subtle">
                {t("motionAmplification.noCameraSettings")}
              </Text>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </Modal>
  );
}
